import { House } from '@/client/types.gen';

export type PropertySortMode =
	| 'recent-desc'
	| 'recent-asc'
	| 'reference-asc'
	| 'reference-desc'
	| 'address-asc'
	| 'address-desc'
	| 'year-built-desc'
	| 'year-built-asc';

export const PROPERTY_SORT_OPTIONS = [
	{ value: 'recent-desc', label: 'Recently added' },
	{ value: 'recent-asc', label: 'Oldest added' },
	{ value: 'reference-asc', label: 'Reference / name A-Z' },
	{ value: 'reference-desc', label: 'Reference / name Z-A' },
	{ value: 'address-asc', label: 'Address A-Z' },
	{ value: 'address-desc', label: 'Address Z-A' },
	{ value: 'year-built-desc', label: 'Year built newest' },
	{ value: 'year-built-asc', label: 'Year built oldest' },
] as const satisfies ReadonlyArray<{ value: PropertySortMode; label: string }>;

const compareText = (left: string | null | undefined, right: string | null | undefined) => {
	return (left || '').localeCompare(right || '', undefined, { sensitivity: 'base', numeric: true });
};

const getYearBuilt = (property: House) => {
	const year = property.date_built ? Number.parseInt(property.date_built.slice(0, 4), 10) : Number.NaN;
	return Number.isNaN(year) ? null : year;
};

const compareYearBuilt = (left: House, right: House) => {
	const leftYear = getYearBuilt(left);
	const rightYear = getYearBuilt(right);

	if (leftYear === null && rightYear === null) return 0;
	if (leftYear === null) return 1;
	if (rightYear === null) return -1;
	return rightYear - leftYear;
};

const getRelevantCreatedAt = (property: House) => {
	return property.user_role?.created_at ?? property.date_created;
};

export const sortProperties = <T extends House>(properties: T[], sortMode: PropertySortMode): T[] => {
	return [...properties].sort((left, right) => {
		switch (sortMode) {
			case 'reference-asc':
				return compareText(left.name, right.name) || compareText(left.address, right.address);
			case 'reference-desc':
				return compareText(right.name, left.name) || compareText(left.address, right.address);
			case 'address-asc':
				return compareText(left.address, right.address) || compareText(left.name, right.name);
			case 'address-desc':
				return compareText(right.address, left.address) || compareText(left.name, right.name);
			case 'year-built-desc':
				return compareYearBuilt(left, right) || compareText(left.address, right.address);
			case 'year-built-asc':
				return compareYearBuilt(right, left) || compareText(left.address, right.address);
			case 'recent-asc':
				return (
					new Date(getRelevantCreatedAt(left)).getTime() - new Date(getRelevantCreatedAt(right)).getTime() ||
					compareText(left.address, right.address)
				);
			case 'recent-desc':
			default:
				return (
					new Date(getRelevantCreatedAt(right)).getTime() - new Date(getRelevantCreatedAt(left)).getTime() ||
					compareText(left.address, right.address)
				);
		}
	});
};
